import { SellerBehaviorType, BehaviorAnalysis } from './types';

export class BehaviorDetector {
  private static readonly BEHAVIOR_PATTERNS = {
    refusal_due_to_tracking: [
      'tracking shows', 'delivered according to', 'courier confirmed',
      'tracking number', 'delivery confirmation', 'marked as delivered'
    ],
    extraordinary_circumstance_defense: [
      'beyond our control', 'extraordinary circumstances', 'operational issues',
      'weather conditions', 'air traffic control', 'unforeseeable'
    ],
    policy_citation: [
      'our policy states', 'terms and conditions', 'according to policy',
      'per our terms', 'policy clearly states', 'as outlined in'
    ],
    milestone_dispute: [
      'milestone completed', 'work was delivered', 'contract fulfilled',
      'agreed specifications', 'scope of work', 'additional work requires'
    ],
    time_tracking_claim: [
      'time was tracked', 'work diary shows', 'hours logged',
      'time tracker', 'billable hours', 'logged time'
    ],
    contract_enforcement: [
      'contract states', 'legally binding', 'agreement specifies',
      'committed to', 'contractual obligation', 'signed agreement'
    ],
    rescheduling_claim: [
      'event rescheduled', 'new date', 'postponed',
      'venue change', 'relocated', 'moved to'
    ],
    trial_period_defense: [
      'trial period', 'free trial', 'subscription trial',
      'trial expired', 'promotional period', 'trial terms'
    ],
    quality_dispute: [
      'meets requirements', 'as requested', 'specifications met',
      'quality standards', 'delivered as agreed', 'subjective opinion'
    ],
    partial_compromise: [
      'partial refund', 'credit towards', 'discount on next',
      'halfway solution', 'compromise', 'meet in the middle'
    ],
    full_acceptance: [
      'full refund', 'completely understand', 'apologize for',
      'issue a refund', 'process your refund', 'refund approved'
    ],
    deflection: [
      'contact support', 'technical issue', 'system error',
      'not our fault', 'third party', 'someone else'
    ],
    stalling: [
      'investigating', 'looking into', 'need more time',
      'escalating internally', 'reviewing', 'checking with team'
    ]
  };

  static detectBehavior(sellerResponse: string): BehaviorAnalysis {
    const response = sellerResponse.toLowerCase();
    const behaviorScores: Record<SellerBehaviorType, number> = {} as any;

    // Calculate scores for each behavior
    Object.entries(this.BEHAVIOR_PATTERNS).forEach(([behavior, patterns]) => {
      const score = patterns.reduce((acc, pattern) => {
        return acc + (response.includes(pattern.toLowerCase()) ? 1 : 0);
      }, 0);
      
      behaviorScores[behavior as SellerBehaviorType] = score / patterns.length;
    });

    // Find the behavior with highest score
    const detectedBehavior = Object.entries(behaviorScores).reduce((max, [behavior, score]) => {
      return score > max.score ? { behavior: behavior as SellerBehaviorType, score } : max;
    }, { behavior: 'deflection' as SellerBehaviorType, score: 0 });

    return {
      behavior: detectedBehavior.behavior,
      confidence: Math.min(detectedBehavior.score * 100, 95), // Cap at 95%
      reasoning: this.generateReasoning(detectedBehavior.behavior, sellerResponse),
      suggested_response_strategy: this.getSuggestedStrategy(detectedBehavior.behavior),
      escalation_recommended: this.shouldEscalate(detectedBehavior.behavior, detectedBehavior.score)
    };
  }

  private static generateReasoning(behavior: SellerBehaviorType, response: string): string {
    const reasoningMap: Record<SellerBehaviorType, string> = {
      refusal_due_to_tracking: 'Seller is using delivery tracking as primary defense against refund claim',
      extraordinary_circumstance_defense: 'Seller claims circumstances were outside their control',
      policy_citation: 'Seller is referencing company policies to deny the request',
      milestone_dispute: 'Seller claims work was completed according to agreed milestones',
      time_tracking_claim: 'Seller is using time tracking data to justify work completion',
      contract_enforcement: 'Seller is strictly enforcing contractual terms',
      rescheduling_claim: 'Seller claims event was rescheduled rather than canceled',
      trial_period_defense: 'Seller is citing trial period terms to deny refund',
      quality_dispute: 'Seller disputes the quality assessment of delivered work/service',
      partial_compromise: 'Seller is offering a partial solution or compromise',
      full_acceptance: 'Seller has accepted responsibility and agreed to refund',
      deflection: 'Seller is deflecting responsibility to external factors',
      stalling: 'Seller appears to be delaying resolution without clear commitment'
    };

    return reasoningMap[behavior];
  }

  private static getSuggestedStrategy(behavior: SellerBehaviorType): string {
    const strategyMap: Record<SellerBehaviorType, string> = {
      refusal_due_to_tracking: 'Provide evidence that item was not received despite tracking, cite buyer protection policies',
      extraordinary_circumstance_defense: 'Challenge whether circumstances were truly extraordinary and unforeseeable',
      policy_citation: 'Reference consumer protection laws that may override company policies',
      milestone_dispute: 'Document specific deliverables that were not met according to original agreement',
      time_tracking_claim: 'Focus on quality and deliverables rather than time spent',
      contract_enforcement: 'Cite consumer protection laws and unfair contract terms',
      rescheduling_claim: 'Argue that significant changes constitute a different event',
      trial_period_defense: 'Reference unauthorized charges and proper trial disclosure requirements',
      quality_dispute: 'Provide objective evidence and comparison to agreed specifications',
      partial_compromise: 'Evaluate if partial offer is fair, negotiate for better terms',
      full_acceptance: 'Accept the resolution and ensure proper processing',
      deflection: 'Redirect responsibility back to seller and demand direct resolution',
      stalling: 'Set clear deadlines and threaten escalation if no progress'
    };

    return strategyMap[behavior];
  }

  private static shouldEscalate(behavior: SellerBehaviorType, confidence: number): boolean {
    const escalationBehaviors: SellerBehaviorType[] = [
      'policy_citation',
      'contract_enforcement', 
      'deflection',
      'stalling'
    ];

    return escalationBehaviors.includes(behavior) && confidence > 0.3;
  }
}